import React, { useState, useEffect, useRef } from 'react';

import SideNav from './components/SideNav.js';
import HeroSection from './sections/HeroSection.js';
import IntroSection from './sections/IntroSection.js';
import ServicesSection from './sections/ServicesSection.js';
import WhoIAmSection from './sections/WhoIAmSection.js';
import BeliefSection from './sections/BeliefSection.js';
import WhyMentorSection from './sections/WhyMentorSection.js';
import ExperienceSection from './sections/ExperienceSection.js';
import IndustriesSection from './sections/IndustriesSection.js';
import Page9 from './sections/Page9.js';
import EducationSection from './sections/EducationSection.js';
import ContactSection from './sections/ContactSection.js';

import './App.css';
import './index.css'

const HORIZONTAL_SECTION_IDS = ['hero', 'intro', 'services'];

export default function App() {
    const [activeSection, setActiveSection] = useState('hero');
    const horizontalContainerRef = useRef(null);
    const trackRef = useRef(null);
    const verticalContentRef = useRef(null);
    
    const HORIZONTAL_PAGES = 3;
    const HORIZONTAL_SCROLL_HEIGHT = `${HORIZONTAL_PAGES * 100}vh`;

    useEffect(() => {
        const handleScroll = () => {
            if (!horizontalContainerRef.current || !trackRef.current) return;

            const container = horizontalContainerRef.current;
            const { top, bottom } = container.getBoundingClientRect();
            
            const scrollableHeight = container.clientHeight - window.innerHeight;
            const scrollProgress = Math.max(0, Math.min(1, -top / scrollableHeight));
            
            const trackWidth = trackRef.current.scrollWidth;
            const newTransformX = -scrollProgress * (trackWidth - window.innerWidth);
            
            trackRef.current.style.transform = `translateX(${newTransformX}px)`;

            // While pinned inside the horizontal-scroll zone, pick whichever
            // of the 3 pages currently covers more than half the screen —
            // not just whichever has started to peek into view.
            const isPinned = top <= 0 && bottom > window.innerHeight;
            if (isPinned) {
                const pageIndex = Math.min(
                    HORIZONTAL_SECTION_IDS.length - 1,
                    Math.round(scrollProgress * (HORIZONTAL_SECTION_IDS.length - 1))
                );
                setActiveSection(HORIZONTAL_SECTION_IDS[pageIndex]);
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll();
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        const id = entry.target.id;
                        if (id) setActiveSection(id);
                    }
                });
            },
            { rootMargin: '-50% 0px -50% 0px', threshold: 0 }
        );

        // Only the vertically-stacked sections are observed here — the 3
        // horizontally-scrolled pages (hero/intro/services) are handled by
        // the scroll-progress logic above instead, since their on-screen
        // position comes from a transform, not real vertical scroll.
        const elementsToObserve = verticalContentRef.current
            ? verticalContentRef.current.querySelectorAll('section[id]')
            : [];

        elementsToObserve.forEach(el => observer.observe(el));
        return () => elementsToObserve.forEach(el => observer.unobserve(el));
    }, []);

    const scrollToSection = (id) => {
        const horizontalIndex = HORIZONTAL_SECTION_IDS.indexOf(id);

        // hero/intro/services share the same DOM position (they're moved
        // horizontally via transform), so scrollIntoView can't tell them
        // apart — it always lands on page 1. Instead, scroll the window to
        // the exact vertical offset that produces this page's scroll progress.
        if (horizontalIndex !== -1 && horizontalContainerRef.current) {
            const container = horizontalContainerRef.current;
            const scrollableHeight = container.clientHeight - window.innerHeight;
            const progress = horizontalIndex / (HORIZONTAL_SECTION_IDS.length - 1);
            const containerTopAbs = window.scrollY + container.getBoundingClientRect().top;
            const targetScrollY = containerTopAbs + progress * scrollableHeight;

            window.scrollTo({ top: targetScrollY, behavior: 'smooth' });
            return;
        }

        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    };

    return (
        <main className="pl-14 sm:pl-16 md:pl-20 bg-brand-black">
            <SideNav activeSection={activeSection} scrollToSection={scrollToSection} />
            
            <div ref={horizontalContainerRef} style={{ height: HORIZONTAL_SCROLL_HEIGHT }}>
                <div className="sticky top-0 h-screen w-full overflow-hidden">
                    <div ref={trackRef} className="flex h-full w-[300vw]">
                        <HeroSection />
                        <IntroSection />
                        <ServicesSection />
                    </div>
                </div>
            </div>

            <div ref={verticalContentRef}>
                <WhoIAmSection />
                <BeliefSection />
                <WhyMentorSection />
                <ExperienceSection />
                <IndustriesSection />
                <Page9 />
                <EducationSection />
                <ContactSection />
               

            </div>
        </main>
    );
}
